import React, { useCallback, useState, useEffect } from 'react'
import { Dialog } from '@headlessui/react'
import { useDispatch, useSelector } from 'react-redux'
import Button from '@atlaskit/button'
import Spinner from '@atlaskit/spinner'
import Tooltip from '@atlaskit/tooltip'
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import { updateTestCaseExecutionAsync, updateTestStepExecutionAsync, updateExecutionMetadataCount } from '../../store/slices/testScenarioSlice'
import { selectProjectStatuses } from '../../store/slices/projectStatusesSlice'
import { useNotification } from '../../contexts/NotificationContext'
import { StatusSelector } from '../common/StatusSelector'
import { CommentItem } from '../common/CommentItem'
import { AddComment } from '../common/AddComment'
import { AttachmentItem } from '../common/AttachmentItem'
import { FileUpload } from '../common/FileUpload'
import { BugItem } from '../common/BugItem'
import { BugLinkInput } from '../common/BugLinkInput'
import { AuditLogItem } from '../common/AuditLogItem'
import {
  getTestCaseExecutionComments,
  createTestCaseExecutionComment,
  getTestStepExecutionComments,
  createTestStepExecutionComment,
  updateComment,
  deleteComment
} from '../../api/commentsApi'
import {
  getTestCaseExecutionAttachments,
  uploadTestCaseExecutionAttachment,
  getTestStepExecutionAttachments,
  uploadTestStepExecutionAttachment,
  downloadAttachment,
  deleteAttachment,
  getAttachmentPreviewBlobUrl
} from '../../api/attachmentsApi'
import {
  getTestCaseExecutionBugs,
  getTestStepExecutionBugs,
  linkBugToTestCaseExecution,
  linkBugToTestStepExecution,
  unlinkBug
} from '../../api/bugsApi'
import {
  getTestCaseExecutionAuditLog,
  getTestStepExecutionAuditLog
} from '../../api/auditApi'
import { prefetchUsers } from '../../utils/userCache'
import { getJiraIssueByKey, openCreateIssueDialog } from '../../utils/jiraContext'

/**
 * EXECUTION DETAILS MODAL
 *
 * Jira-style modal for viewing and editing execution details with tabbed interface.
 * Organized into tabs: Status, Comments, Attachments, Bugs, Audit Log.
 *
 * Features:
 * - Jira-style header with status badge and close button
 * - Tabbed interface for organized content
 * - Status selection grid to change execution status
 * - Comments tab with add/edit/delete
 * - Attachments tab with upload/download/delete
 * - Bugs tab to link/unlink Jira issues
 * - Audit log tab showing execution history
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Callback to close modal
 * @param {object} execution - Execution data { testCaseId, testStepId, runId, execution: { id, statusId, statusName, statusColor } }
 */
export function ExecutionDetailsModal({ isOpen, onClose, execution }) {
  const dispatch = useDispatch()
  const { showError, showSuccess } = useNotification()

  // Get data from Redux
  const testScenarioId = useSelector(state => state.testScenario.testScenarioId)
  const projectStatuses = useSelector(selectProjectStatuses)

  // Jira Connect injects xdm_e as the host base URL (e.g. https://acme.atlassian.net)
  const jiraBaseUrl = new URLSearchParams(window.location.search).get('xdm_e')

  // Tab state - 0: Status, 1: Comments, 2: Attachments, 3: Bugs, 4: Audit
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)

  // Status update state
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  // Local status state - tracks current status so UI updates immediately after change
  const [currentStatus, setCurrentStatus] = useState(null)

  // Comments state
  const [comments, setComments] = useState([])
  const [isLoadingComments, setIsLoadingComments] = useState(false)
  const [showAddComment, setShowAddComment] = useState(false)

  // Attachments state
  const [attachments, setAttachments] = useState([])
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false)

  // Bugs state
  const [bugs, setBugs] = useState([])
  const [isLoadingBugs, setIsLoadingBugs] = useState(false)

  // Audit log state
  const [auditLog, setAuditLog] = useState([])
  const [isLoadingAuditLog, setIsLoadingAuditLog] = useState(false)

  // Image preview state
  const [previewImage, setPreviewImage] = useState(null)
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)

  // Determine if this is a test case or test step execution
  const isTestCase = !execution?.testStepId
  const executionId = execution?.execution?.id

  // Initial counts from props - shown immediately while API loads
  // These come from the initial scenario data load, so we can display them instantly
  const initialCommentCount = execution?.execution?.commentCount || 0
  const initialBugCount = execution?.execution?.bugCount || 0
  const initialAttachmentCount = execution?.execution?.attachmentCount || 0

  /**
   * Returns initial count while loading, then array length once loaded.
   * This provides instant feedback to users by showing cached counts from
   * the initial data load, then switching to actual array length after API responds.
   */
  const getDisplayCount = (fetchedArray, isLoading, initialCount) => {
    if (isLoading) return initialCount
    return fetchedArray.length
  }

  /**
   * INITIALIZE CURRENT STATUS
   * Sets the local status state from execution prop when modal opens.
   */
  useEffect(() => {
    if (isOpen && execution?.execution) {
      setCurrentStatus({
        statusId: execution.execution.statusId,
        statusName: execution.execution.statusName,
        statusColor: execution.execution.statusColor
      })
    }
  }, [isOpen, execution])

  /**
   * LOAD ALL DATA
   * Fetches data for all tabs when modal opens.
   */
  useEffect(() => {
    if (isOpen && execution && testScenarioId) {
      const { testCaseId, testStepId, runId } = execution

      // Load comments (uses runId + testCaseId/testStepId)
      setIsLoadingComments(true)
      const commentsPromise = isTestCase
        ? getTestCaseExecutionComments(runId, testCaseId)
        : getTestStepExecutionComments(runId, testStepId)

      commentsPromise
        .then(data => {
          setComments(data || [])
          // Prefetch user details for all comment authors
          const accountIds = data?.map(c => c.createdBy?.accountId).filter(Boolean) || []
          if (accountIds.length > 0) {
            prefetchUsers(accountIds)
          }
        })
        .catch(error => {
          console.error('Failed to load comments:', error)
          setComments([])
        })
        .finally(() => setIsLoadingComments(false))

      // Load attachments (uses runId + testCaseId/testStepId)
      setIsLoadingAttachments(true)
      const attachmentsPromise = isTestCase
        ? getTestCaseExecutionAttachments(runId, testCaseId)
        : getTestStepExecutionAttachments(runId, testStepId)

      attachmentsPromise
        .then(data => {
          setAttachments(data || [])
          // Prefetch user details for all uploaders
          const accountIds = data?.map(a => a.createdBy).filter(Boolean) || []
          if (accountIds.length > 0) {
            prefetchUsers(accountIds)
          }
        })
        .catch(error => {
          console.error('Failed to load attachments:', error)
          setAttachments([])
        })
        .finally(() => setIsLoadingAttachments(false))

      // Load bugs (uses testCaseId/testStepId) and enrich with Jira data
      setIsLoadingBugs(true)
      const bugsPromise = isTestCase
        ? getTestCaseExecutionBugs(testScenarioId, runId, testCaseId)
        : getTestStepExecutionBugs(testScenarioId, runId, testStepId)

      bugsPromise
        .then(async (data) => {
          if (!data || data.length === 0) {
            setBugs([])
            return
          }
          // Enrich bugs with Jira issue details (summary, status, priority, etc.)
          const enrichedBugs = await Promise.all(
            data.map(async (bug) => {
              const jiraDetails = await getJiraIssueByKey(bug.jiraIssueKey)
              if (jiraDetails) {
                return { ...bug, ...jiraDetails, jiraFetchFailed: false }
              }
              return { ...bug, jiraFetchFailed: true }
            })
          )
          setBugs(enrichedBugs)
        })
        .catch(error => {
          console.error('Failed to load bugs:', error)
          setBugs([])
        })
        .finally(() => setIsLoadingBugs(false))

      // Load audit log
      setIsLoadingAuditLog(true)
      const auditPromise = isTestCase
        ? getTestCaseExecutionAuditLog(testScenarioId, runId, testCaseId)
        : getTestStepExecutionAuditLog(testScenarioId, runId, testStepId)

      auditPromise
        .then(data => {
          setAuditLog(data || [])
          // Prefetch user details for all audit entries
          const accountIds = data?.map(e => e.changedByAccountId).filter(Boolean) || []
          if (accountIds.length > 0) {
            prefetchUsers(accountIds)
          }
        })
        .catch(error => {
          console.error('Failed to load audit log:', error)
          setAuditLog([])
        })
        .finally(() => setIsLoadingAuditLog(false))
    }
  }, [isOpen, execution, testScenarioId, isTestCase])

  /**
   * Reset state when modal closes
   */
  useEffect(() => {
    if (!isOpen) {
      setComments([])
      setAttachments([])
      setBugs([])
      setAuditLog([])
      setShowAddComment(false)
      setSelectedTabIndex(0)
      setPreviewImage(null)
      setCurrentStatus(null)
      // Clean up blob URL to free memory
      if (previewBlobUrl) {
        window.URL.revokeObjectURL(previewBlobUrl)
        setPreviewBlobUrl(null)
      }
    }
  }, [isOpen, previewBlobUrl])

  // ============================================================================
  // STATUS HANDLERS
  // ============================================================================

  /**
   * HANDLE STATUS CHANGE
   * Updates the execution status. Pass null to clear the status.
   */
  const handleStatusChange = useCallback(async (status) => {
    if (!execution || !testScenarioId) return

    setIsUpdatingStatus(true)
    try {
      // Handle clearing status (status is null)
      const statusId = status?.id || null
      const statusName = status?.name || null
      const statusColor = status?.color || null

      const params = {
        testScenarioId,
        testRunId: execution.runId,
        testCaseId: execution.testCaseId,
        statusId,
        statusName,
        statusColor
      }

      if (isTestCase) {
        await dispatch(updateTestCaseExecutionAsync(params)).unwrap()
      } else {
        await dispatch(updateTestStepExecutionAsync({
          ...params,
          testStepId: execution.testStepId
        })).unwrap()
      }

      // Update local status state so UI reflects the change immediately
      setCurrentStatus(status ? {
        statusId: status.id,
        statusName: status.name,
        statusColor: status.color
      } : null)

      // Reload audit log to show the change
      const auditPromise = isTestCase
        ? getTestCaseExecutionAuditLog(testScenarioId, execution.runId, execution.testCaseId)
        : getTestStepExecutionAuditLog(testScenarioId, execution.runId, execution.testStepId)

      auditPromise
        .then(data => setAuditLog(data || []))
        .catch(error => console.error('Failed to reload audit log:', error))
    } catch (error) {
      console.error('Failed to update status:', error)
      showError('Update Failed', error.message || 'Failed to update status')
    } finally {
      setIsUpdatingStatus(false)
    }
  }, [execution, testScenarioId, isTestCase, dispatch, showError])

  // ============================================================================
  // COMMENT HANDLERS
  // ============================================================================

  /**
   * HANDLE ADD COMMENT
   */
  const handleAddComment = useCallback(async (content) => {
    if (!execution) return

    const { testCaseId, testStepId, runId } = execution

    try {
      const newComment = isTestCase
        ? await createTestCaseExecutionComment(runId, testCaseId, content)
        : await createTestStepExecutionComment(runId, testStepId, content)
      setComments(prev => [...prev, newComment])
      setShowAddComment(false)
      showSuccess('Comment Added', 'Your comment has been added.')
      // Update Redux metadata count so ExecutionCell icon updates immediately
      dispatch(updateExecutionMetadataCount({
        testCaseId: execution.testCaseId,
        testStepId: execution.testStepId || null,
        runId: execution.runId,
        field: 'commentCount',
        delta: 1
      }))
    } catch (error) {
      console.error('Failed to add comment:', error)
      showError('Error', 'Failed to add comment. Please try again.')
      throw error
    }
  }, [execution, isTestCase, dispatch, showError, showSuccess])

  /**
   * HANDLE EDIT COMMENT
   */
  const handleEditComment = useCallback(async (commentId, content) => {
    try {
      const updatedComment = await updateComment(commentId, content)
      setComments(prev => prev.map(c => c.id === commentId ? updatedComment : c))
      showSuccess('Comment Updated', 'Your comment has been updated.')
    } catch (error) {
      console.error('Failed to edit comment:', error)
      showError('Error', 'Failed to update comment. Please try again.')
      throw error
    }
  }, [showError, showSuccess])

  /**
   * HANDLE DELETE COMMENT
   */
  const handleDeleteComment = useCallback(async (commentId) => {
    try {
      await deleteComment(commentId)
      setComments(prev => prev.filter(c => c.id !== commentId))
      showSuccess('Comment Deleted', 'The comment has been deleted.')
      // Update Redux metadata count so ExecutionCell icon updates immediately
      dispatch(updateExecutionMetadataCount({
        testCaseId: execution.testCaseId,
        testStepId: execution.testStepId || null,
        runId: execution.runId,
        field: 'commentCount',
        delta: -1
      }))
    } catch (error) {
      console.error('Failed to delete comment:', error)
      showError('Error', 'Failed to delete comment. Please try again.')
      throw error
    }
  }, [execution, dispatch, showError, showSuccess])

  // ============================================================================
  // ATTACHMENT HANDLERS
  // ============================================================================

  /**
   * HANDLE UPLOAD ATTACHMENT
   */
  const handleUploadAttachment = useCallback(async (file) => {
    if (!execution) return

    const { testCaseId, testStepId, runId } = execution

    try {
      const newAttachment = isTestCase
        ? await uploadTestCaseExecutionAttachment(runId, testCaseId, file)
        : await uploadTestStepExecutionAttachment(runId, testStepId, file)
      setAttachments(prev => [...prev, newAttachment])
      showSuccess('File Uploaded', `"${file.name}" has been uploaded.`)
      // Update Redux metadata count so ExecutionCell icon updates immediately
      dispatch(updateExecutionMetadataCount({
        testCaseId: execution.testCaseId,
        testStepId: execution.testStepId || null,
        runId: execution.runId,
        field: 'attachmentCount',
        delta: 1
      }))
    } catch (error) {
      console.error('Failed to upload attachment:', error)
      const errorMessage = error.displayMessage || 'Failed to upload file. Please try again.'
      showError('Upload Failed', errorMessage)
      throw error
    }
  }, [execution, isTestCase, dispatch, showError, showSuccess])

  /**
   * HANDLE DOWNLOAD ATTACHMENT
   */
  const handleDownloadAttachment = useCallback(async (attachment) => {
    try {
      await downloadAttachment(attachment.id, attachment.fileName)
    } catch (error) {
      console.error('Failed to download attachment:', error)
      showError('Download Failed', 'Failed to download file. Please try again.')
    }
  }, [showError])

  /**
   * HANDLE PREVIEW ATTACHMENT
   */
  const handlePreviewAttachment = useCallback(async (attachment) => {
    setPreviewImage(attachment)
    setIsLoadingPreview(true)

    // Clean up previous blob URL if exists
    if (previewBlobUrl) {
      window.URL.revokeObjectURL(previewBlobUrl)
    }

    try {
      const blobUrl = await getAttachmentPreviewBlobUrl(attachment.id)
      setPreviewBlobUrl(blobUrl)
    } catch (error) {
      console.error('Failed to load preview:', error)
      showError('Preview Failed', 'Failed to load image preview.')
      setPreviewImage(null)
    } finally {
      setIsLoadingPreview(false)
    }
  }, [previewBlobUrl, showError])

  /**
   * HANDLE DELETE ATTACHMENT
   */
  const handleDeleteAttachment = useCallback(async (attachmentId) => {
    try {
      await deleteAttachment(attachmentId)
      setAttachments(prev => prev.filter(a => a.id !== attachmentId))
      showSuccess('File Deleted', 'The attachment has been deleted.')
      // Update Redux metadata count so ExecutionCell icon updates immediately
      dispatch(updateExecutionMetadataCount({
        testCaseId: execution.testCaseId,
        testStepId: execution.testStepId || null,
        runId: execution.runId,
        field: 'attachmentCount',
        delta: -1
      }))
    } catch (error) {
      console.error('Failed to delete attachment:', error)
      showError('Error', 'Failed to delete attachment. Please try again.')
      throw error
    }
  }, [execution, dispatch, showError, showSuccess])

  // ============================================================================
  // BUG HANDLERS
  // ============================================================================

  /**
   * HANDLE LINK BUG
   * Links a bug and enriches it with Jira issue details (summary, status, etc.)
   */
  const handleLinkBug = useCallback(async (issueKey) => {
    if (!execution || !testScenarioId) return

    try {
      const { testCaseId, testStepId, runId } = execution
      const linkFn = isTestCase ? linkBugToTestCaseExecution : linkBugToTestStepExecution
      const entityId = isTestCase ? testCaseId : testStepId
      const newBug = await linkFn(testScenarioId, runId, entityId, issueKey)

      // Enrich the new bug with Jira issue details
      const jiraDetails = await getJiraIssueByKey(issueKey)
      const enrichedBug = jiraDetails
        ? { ...newBug, ...jiraDetails, jiraFetchFailed: false }
        : { ...newBug, jiraFetchFailed: true }

      setBugs(prev => [...prev, enrichedBug])
      showSuccess('Bug Linked', `${issueKey} has been linked.`)
      // Update Redux metadata count so ExecutionCell icon updates immediately
      dispatch(updateExecutionMetadataCount({
        testCaseId: execution.testCaseId,
        testStepId: execution.testStepId || null,
        runId: execution.runId,
        field: 'bugCount',
        delta: 1
      }))

      // Reload audit log to show the change
      const auditPromise = isTestCase
        ? getTestCaseExecutionAuditLog(testScenarioId, runId, testCaseId)
        : getTestStepExecutionAuditLog(testScenarioId, runId, testStepId)

      auditPromise
        .then(data => setAuditLog(data || []))
        .catch(error => console.error('Failed to reload audit log:', error))
    } catch (error) {
      console.error('Failed to link bug:', error)
      throw error
    }
  }, [execution, testScenarioId, isTestCase, dispatch, showSuccess])

  /**
   * HANDLE UNLINK BUG
   */
  const handleUnlinkBug = useCallback(async (bugId) => {
    if (!execution || !testScenarioId) return

    try {
      await unlinkBug(testScenarioId, execution.runId, bugId)
      setBugs(prev => prev.filter(b => b.id !== bugId))
      showSuccess('Bug Unlinked', 'The bug has been unlinked.')
      // Update Redux metadata count so ExecutionCell icon updates immediately
      dispatch(updateExecutionMetadataCount({
        testCaseId: execution.testCaseId,
        testStepId: execution.testStepId || null,
        runId: execution.runId,
        field: 'bugCount',
        delta: -1
      }))

      // Reload audit log to show the change
      const auditPromise = isTestCase
        ? getTestCaseExecutionAuditLog(testScenarioId, execution.runId, execution.testCaseId)
        : getTestStepExecutionAuditLog(testScenarioId, execution.runId, execution.testStepId)

      auditPromise
        .then(data => setAuditLog(data || []))
        .catch(error => console.error('Failed to reload audit log:', error))
    } catch (error) {
      console.error('Failed to unlink bug:', error)
      showError('Error', 'Failed to unlink bug. Please try again.')
      throw error
    }
  }, [execution, testScenarioId, isTestCase, dispatch, showSuccess, showError])

  /**
   * HANDLE CREATE BUG
   * Opens Jira's native create issue dialog.
   * After the user creates an issue, automatically links it to this execution.
   */
  const handleCreateBug = useCallback(async () => {
    try {
      // Open Jira's create issue dialog
      const issue = await openCreateIssueDialog({
        // Optionally pre-fill description with execution context
        description: `Created from test execution`
      })

      // If user created an issue, link it
      if (issue && issue.key) {
        await handleLinkBug(issue.key)
      }
    } catch (error) {
      console.error('Failed to create issue:', error)
      showError('Error', 'Failed to open create issue dialog.')
    }
  }, [handleLinkBug, showError])

  // ============================================================================
  // TAB CHANGE HANDLER
  // ============================================================================

  /**
   * HANDLE TAB CHANGE
   */
  const handleTabChange = useCallback((index) => {
    setSelectedTabIndex(index)
  }, [])

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} className="headless-dialog-root">
        {/* Backdrop */}
        <div
          className="headless-dialog-backdrop"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'var(--ds-blanket)',
            zIndex: 1000
          }}
          aria-hidden="true"
        />

        {/* Modal Container - centered with flexbox */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            pointerEvents: 'none'
          }}
        >
          <Dialog.Panel
            style={{
              backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
              borderRadius: '8px',
              boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
              width: '680px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'auto'
            }}
          >
            {execution && (
              <>
                {/* HEADER */}
                <div
                  style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid var(--ds-border, #DFE1E6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0
                  }}
                >
                  {/* Left: Title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <Dialog.Title
                      style={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: 'var(--ds-text, #172B4D)',
                        margin: 0
                      }}
                    >
                      Execution Details
                    </Dialog.Title>
                  </div>

                  {/* Right: Close button */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <Tooltip content="Close">
                      <Button
                        appearance="subtle"
                        iconBefore={<CrossIcon label="Close" size="medium" />}
                        onClick={onClose}
                      />
                    </Tooltip>
                  </div>
                </div>

                {/* TABS */}
                <Tabs
                  id="execution-details-tabs"
                  onChange={handleTabChange}
                  selected={selectedTabIndex}
                >
                  {/* Tab list with padding */}
                  <div style={{ padding: '0 24px', borderBottom: '1px solid var(--ds-border, #DFE1E6)' }}>
                    <TabList>
                      <Tab>Status</Tab>
                      <Tab>Comments {getDisplayCount(comments, isLoadingComments, initialCommentCount) > 0 && `(${getDisplayCount(comments, isLoadingComments, initialCommentCount)})`}</Tab>
                      <Tab>Attachments {getDisplayCount(attachments, isLoadingAttachments, initialAttachmentCount) > 0 && `(${getDisplayCount(attachments, isLoadingAttachments, initialAttachmentCount)})`}</Tab>
                      <Tab>Bugs {getDisplayCount(bugs, isLoadingBugs, initialBugCount) > 0 && `(${getDisplayCount(bugs, isLoadingBugs, initialBugCount)})`}</Tab>
                      <Tab>Audit Log</Tab>
                    </TabList>
                  </div>

                  {/* STATUS TAB PANEL */}
                  <TabPanel>
                    {/* Fixed minimum height prevents modal height shift when status changes trigger loading states */}
                    <div
                      style={{
                        padding: '20px 24px',
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 180px)',
                        minHeight: '280px'
                      }}
                    >
                      <StatusSelector
                        statuses={projectStatuses}
                        currentStatusId={currentStatus?.statusId}
                        onStatusChange={handleStatusChange}
                        isLoading={isUpdatingStatus}
                      />
                    </div>
                  </TabPanel>

                  {/* COMMENTS TAB PANEL */}
                  <TabPanel>
                    <div
                      style={{
                        padding: '20px 24px',
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 180px)'
                      }}
                    >
                      {isLoadingComments ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                          <Spinner size="small" />
                        </div>
                      ) : (
                        <>
                          {/* Add Comment Button - Always visible at top */}
                          {!showAddComment && (
                            <div style={{ marginBottom: '16px' }}>
                              <Button
                                appearance="subtle"
                                onClick={() => setShowAddComment(true)}
                              >
                                + Add Comment
                              </Button>
                            </div>
                          )}

                          {/* Add Comment Form */}
                          {showAddComment && (
                            <AddComment
                              onSubmit={handleAddComment}
                              onCancel={() => setShowAddComment(false)}
                              placeholder="Add a comment..."
                              autoFocus
                            />
                          )}

                          {/* Comment List */}
                          {comments.length > 0 ? (
                            comments.map(comment => (
                              <CommentItem
                                key={comment.id}
                                comment={comment}
                                onEdit={handleEditComment}
                                onDelete={handleDeleteComment}
                              />
                            ))
                          ) : !showAddComment ? (
                            <div
                              style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: 'var(--ds-text-subtlest, #7A869A)',
                                fontSize: '13px'
                              }}
                            >
                              No comments yet. Click "+ Add Comment" to add one.
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </TabPanel>

                  {/* ATTACHMENTS TAB PANEL */}
                  <TabPanel>
                    <div
                      style={{
                        padding: '20px 24px',
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 180px)'
                      }}
                    >
                      {isLoadingAttachments ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                          <Spinner size="small" />
                        </div>
                      ) : (
                        <>
                          {/* File Upload Zone */}
                          <div id="execution-file-upload-wrapper">
                            <FileUpload onUpload={handleUploadAttachment} isActive={selectedTabIndex === 2} />
                          </div>

                          {/* Attachment List */}
                          {attachments.length > 0 && (
                            <div style={{ marginTop: '16px' }}>
                              {attachments.map(attachment => (
                                <AttachmentItem
                                  key={attachment.id}
                                  attachment={attachment}
                                  onPreview={handlePreviewAttachment}
                                  onDownload={handleDownloadAttachment}
                                  onDelete={handleDeleteAttachment}
                                />
                              ))}
                            </div>
                          )}

                          {/* Empty state */}
                          {attachments.length === 0 && (
                            <div
                              style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: 'var(--ds-text-subtlest, #7A869A)',
                                fontSize: '13px'
                              }}
                            >
                              No attachments yet. Drag and drop files above or click to browse.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </TabPanel>

                  {/* BUGS TAB PANEL */}
                  <TabPanel>
                    <div
                      style={{
                        padding: '20px 24px',
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 180px)'
                      }}
                    >
                      {isLoadingBugs ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                          <Spinner size="small" />
                        </div>
                      ) : (
                        <>
                          {/* Bug Link Input with +Bug button */}
                          <BugLinkInput
                            onLink={handleLinkBug}
                            extraButton={
                              <Tooltip content="Create a new Jira issue and link it">
                                <Button
                                  appearance="default"
                                  onClick={handleCreateBug}
                                  style={{ marginTop: '2px' }}
                                >
                                  +Bug
                                </Button>
                              </Tooltip>
                            }
                          />

                          {/* Bug List */}
                          {bugs.length > 0 ? (
                            bugs.map(bug => (
                              <BugItem
                                key={bug.id}
                                bug={bug}
                                onUnlink={handleUnlinkBug}
                                jiraBaseUrl={jiraBaseUrl}
                              />
                            ))
                          ) : (
                            <div
                              style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: 'var(--ds-text-subtlest, #7A869A)',
                                fontSize: '13px'
                              }}
                            >
                              No bugs linked. Enter a Jira issue key above to link one.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </TabPanel>

                  {/* AUDIT LOG TAB PANEL */}
                  <TabPanel>
                    <div
                      style={{
                        padding: '20px 24px',
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 180px)'
                      }}
                    >
                      {isLoadingAuditLog ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                          <Spinner size="small" />
                        </div>
                      ) : auditLog.length > 0 ? (
                        auditLog.map(entry => (
                          <AuditLogItem
                            key={entry.id}
                            entry={entry}
                          />
                        ))
                      ) : (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: 'var(--ds-text-subtlest, #7A869A)',
                            fontSize: '13px'
                          }}
                        >
                          No activity yet. Status changes and bug links will appear here.
                        </div>
                      )}
                    </div>
                  </TabPanel>
                </Tabs>

                {/* Footer - Single Close button */}
                <div
                  style={{
                    padding: '16px 24px',
                    borderTop: '1px solid var(--ds-border, #DFE1E6)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    flexShrink: 0
                  }}
                >
                  <Button
                    appearance="primary"
                    onClick={onClose}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Image Preview Modal */}
      {previewImage && (
        <Dialog
          open={!!previewImage}
          onClose={() => setPreviewImage(null)}
          className="headless-dialog-root"
        >
          <div
            className="headless-dialog-backdrop"
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(9, 30, 66, 0.8)',
              zIndex: 1100
            }}
            aria-hidden="true"
            onClick={() => setPreviewImage(null)}
          />
          <div
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1101,
              pointerEvents: 'none',
              padding: '20px'
            }}
          >
            <Dialog.Panel
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                pointerEvents: 'auto',
                position: 'relative'
              }}
            >
              {isLoadingPreview ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px' }}>
                  <Spinner size="large" />
                </div>
              ) : previewBlobUrl ? (
                <img
                  src={previewBlobUrl}
                  alt={previewImage.fileName}
                  style={{
                    maxWidth: '90vw',
                    maxHeight: '88vh',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    display: 'block'
                  }}
                />
              ) : null}
              <Button
                appearance="primary"
                onClick={() => setPreviewImage(null)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px'
                }}
              >
                Close
              </Button>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}
    </>
  )
}
